import { PaymentPartnerType } from '../../../../../codegen/vehicles';
import { useEffect } from 'react';
import { useCustomIntl } from '../../../../../i18n/i18n';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { useAppDispatch, useAppSelector } from '../../../../../state/hooks';
import {
  AddNewServiceCardForm,
  initialAddNewServiceCardForm,
} from './AddNewServiceCardForm';
import {
  addNewServiceCardDialogError,
  closeAddNewServiceCardDialog,
  DialogMode,
  getPaymentPartnerType,
  isAddNewServiceCardDialogOpen,
  openConfigurationDialog,
} from '../../../state/onboardingUiSlice';
import { DialogFooter } from '../DialogFooter';
import { ServiceCardForm } from './ServiceCardForm';
import { ErrorMessagePanel } from '../../../../../components/ErrorMessagePanel';
import { useEdenredToken } from '../../../../../login/external/edenredUserManager';
import { ERROR_CODE_TO_TRANSLATION_KEY } from '../../../../../i18n/enumMappings';
import { BackendErrors } from '../../../../../models';
import { useCreateServiceCard } from './useCreateServiceCard';
import { useSelectedVehicle } from '../../../../../state/vehicles/useVehicles';
import { FormProvider, useForm } from 'react-hook-form';

export const AddNewServiceCardDialog = () => {
  const dispatch = useAppDispatch();
  const selectedVehicle = useSelectedVehicle();
  const intl = useCustomIntl();
  const isDialogOpen = useAppSelector(isAddNewServiceCardDialogOpen);
  const externalLoginError = useAppSelector(addNewServiceCardDialogError);
  const formUtilsAddServiceCard = useForm<AddNewServiceCardForm>();
  const {
    formState: { isDirty, isValid },
    reset,
  } = formUtilsAddServiceCard;

  const edenredToken = useEdenredToken();

  const selectedPaymentPartnerType = useAppSelector(getPaymentPartnerType);
  const {
    createServiceCard,
    error: addPaymentMethodError,
    isLoading,
    reset: resetServiceCardCreation,
  } = useCreateServiceCard();

  useEffect(() => {
    reset(initialAddNewServiceCardForm);
  }, [isDialogOpen]);

  const isPaymentCardProviderLoginRequired =
    selectedPaymentPartnerType === PaymentPartnerType.UtaPayment &&
    !edenredToken;

  const handleClose = () => {
    resetServiceCardCreation();
    dispatch(closeAddNewServiceCardDialog());
  };

  const handleSubmit = async () => {
    const success = await createServiceCard(formUtilsAddServiceCard);
    if (success) {
      handleClose();
      dispatch(
        openConfigurationDialog(
          selectedVehicle?.paymentMethods.length === 0
            ? DialogMode.FIRST_SERVICE_CARD
            : DialogMode.SERVICE_CARDS,
        ),
      );
    }
  };

  const renderAddNewCardBody = () => {
    const errorCode = addPaymentMethodError?.data?.errorCode as BackendErrors;
    const errorMessage = ERROR_CODE_TO_TRANSLATION_KEY[errorCode];
    const errorValues = {
      vehicleName: addPaymentMethodError?.data?.vehicleName || '',
    };
    return (
      <>
        {(addPaymentMethodError || externalLoginError) && (
          <ErrorMessagePanel
            message={errorMessage}
            values={errorValues}
          />
        )}
        <ServiceCardForm />
      </>
    );
  };

  const showDialog = isDialogOpen && !!selectedVehicle;

  return (
    <FormProvider {...formUtilsAddServiceCard}>
      <Dialog
        data-testid={'add-new-service-card-dialog'}
        bsSize={Dialog.SIZE_SM}
        show={showDialog}
        onClose={handleClose}
        title={intl.formatMessage({
          id: 'onboarding.addServiceCard.dialog.title',
        })}
        body={renderAddNewCardBody()}
        footer={
          <DialogFooter
            showLegalText={false}
            dialogMode={DialogMode.SERVICES}
            onClose={handleClose}
            isSaveButtonEnabled={
              isDirty && isValid && !isPaymentCardProviderLoginRequired
            }
            onSave={handleSubmit}
            isLoading={isLoading}
          />
        }
      />
    </FormProvider>
  );
};
